"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { GroupsRequestParams } from "@/entities/IGroup";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
import type { Mutable, NonNullableNotRequired } from "@/helpers/Types";
import { commonApiFetch } from "@/services/api/common-api";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import SelectGroupModalHeader from "./SelectGroupModalHeader";
import SelectGroupModalSearch from "./SelectGroupModalSearch";
import SelectGroupModalItems from "./SelectGroupModalItems";
const DEFAULT_CONTAINER_CLASSES = "sm:tw-max-w-md tw-relative tw-w-full tw-transform tw-rounded-xl tw-bg-iron-950 tw-text-left tw-shadow-xl tw-transition-all tw-duration-500";
const DEFAULT_BODY_CLASSES = "tw-h-64 tw-overflow-y-auto tw-mt-4 tw-px-4 tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300";
type SelectGroupSearchPanelProps = {
  readonly onClose?: (() => void) | undefined;
  readonly onGroupSelect: (group: ApiGroupFull) => void;
  readonly onGroupClear?: (() => void) | undefined;
  readonly selectedGroupId?: string | null | undefined;
  readonly containerClassName?: string | undefined;
  readonly bodyClassName?: string | undefined;
  readonly showHeader?: boolean | undefined;
  readonly useDefaultContainerStyles?: boolean | undefined;
  readonly useDefaultBodyStyles?: boolean | undefined;
  readonly showIdentitySearch?: boolean | undefined;
  readonly requireSearchQuery?: boolean | undefined;
  readonly idleStateMessage?: string | undefined;
  readonly emptyStateMessage?: string | undefined;
};
export default function SelectGroupSearchPanel(t0) {
  const $ = _c(49);
  const {
    onClose,
    onGroupSelect,
    onGroupClear,
    selectedGroupId,
    containerClassName,
    bodyClassName,
    showHeader: t1,
    useDefaultContainerStyles: t2,
    useDefaultBodyStyles: t3,
    showIdentitySearch: t4,
    requireSearchQuery: t5,
    idleStateMessage,
    emptyStateMessage
  } = t0;
  const showHeader = t1 === undefined ? true : t1;
  const useDefaultContainerStyles = t2 === undefined ? true : t2;
  const useDefaultBodyStyles = t3 === undefined ? true : t3;
  const showIdentitySearch = t4 === undefined ? true : t4;
  const requireSearchQuery = t5 === undefined ? false : t5;
  const handleClose = onClose ?? _temp;
  const t6 = useDefaultContainerStyles ? DEFAULT_CONTAINER_CLASSES : null;
  let t7;
  if ($[0] !== containerClassName || $[1] !== t6) {
    t7 = [t6, containerClassName].filter(Boolean);
    $[0] = containerClassName;
    $[1] = t6;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  const containerClasses = t7.join(" ");
  const t8 = useDefaultBodyStyles ? DEFAULT_BODY_CLASSES : null;
  let t9;
  if ($[3] !== bodyClassName || $[4] !== t8) {
    t9 = [t8, bodyClassName].filter(Boolean);
    $[3] = bodyClassName;
    $[4] = t8;
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  const bodyClasses = t9.join(" ");
  let t10;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      group_name: null,
      author_identity: null
    };
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  const [filters, setFilters] = useState(t10);
  let t11;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = value => {
      setFilters(prev => ({
        ...prev,
        author_identity: value
      }));
    };
    $[7] = t11;
  } else {
    t11 = $[7];
  }
  const onUserSelect = t11;
  let t12;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = value_0 => {
      setFilters(prev_0 => ({
        ...prev_0,
        group_name: value_0
      }));
    };
    $[8] = t12;
  } else {
    t12 = $[8];
  }
  const onFilterNameSearch = t12;
  let t13;
  if ($[9] !== filters.group_name) {
    t13 = filters.group_name?.trim() ?? "";
    $[9] = filters.group_name;
    $[10] = t13;
  } else {
    t13 = $[10];
  }
  const trimmedGroupName = t13;
  let t14;
  if ($[11] !== filters.author_identity) {
    t14 = filters.author_identity?.trim() ?? "";
    $[11] = filters.author_identity;
    $[12] = t14;
  } else {
    t14 = $[12];
  }
  const trimmedAuthorIdentity = t14;
  const hasGroupNameFilter = trimmedGroupName.length > 0;
  const hasUserFilter = showIdentitySearch && trimmedAuthorIdentity.length > 0;
  const isQueryEnabled = requireSearchQuery ? hasGroupNameFilter || hasUserFilter : true;
  let t15;
  if ($[13] !== filters) {
    t15 = [QueryKey.GROUPS, filters];
    $[13] = filters;
    $[14] = t15;
  } else {
    t15 = $[14];
  }
  let t16;
  if ($[15] !== hasGroupNameFilter || $[16] !== showIdentitySearch || $[17] !== trimmedAuthorIdentity || $[18] !== trimmedGroupName) {
    t16 = async () => {
      const params = {};
      if (hasGroupNameFilter) {
        params.group_name = trimmedGroupName;
      }
      if (showIdentitySearch && trimmedAuthorIdentity.length > 0) {
        params.author_identity = trimmedAuthorIdentity;
      }
      return await commonApiFetch({
        endpoint: "groups",
        params
      });
    };
    $[15] = hasGroupNameFilter;
    $[16] = showIdentitySearch;
    $[17] = trimmedAuthorIdentity;
    $[18] = trimmedGroupName;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  let t17;
  if ($[20] !== isQueryEnabled || $[21] !== t15 || $[22] !== t16) {
    t17 = {
      queryKey: t15,
      queryFn: t16,
      placeholderData: keepPreviousData,
      enabled: isQueryEnabled
    };
    $[20] = isQueryEnabled;
    $[21] = t15;
    $[22] = t16;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  const {
    data,
    isFetching
  } = useQuery(t17);
  let t18;
  if ($[24] !== data || $[25] !== isQueryEnabled) {
    t18 = isQueryEnabled ? data ?? [] : [];
    $[24] = data;
    $[25] = isQueryEnabled;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const groups = t18;
  const resolvedEmptyStateMessage = isQueryEnabled ? emptyStateMessage : idleStateMessage ?? emptyStateMessage;
  let t19;
  if ($[27] !== handleClose || $[28] !== showHeader) {
    t19 = showHeader && <SelectGroupModalHeader onClose={handleClose} />;
    $[27] = handleClose;
    $[28] = showHeader;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] !== filters.author_identity || $[31] !== filters.group_name || $[32] !== showIdentitySearch) {
    t20 = <SelectGroupModalSearch groupName={filters.group_name} groupUser={filters.author_identity} onUserSelect={onUserSelect} onFilterNameSearch={onFilterNameSearch} showIdentitySearch={showIdentitySearch} />;
    $[30] = filters.author_identity;
    $[31] = filters.group_name;
    $[32] = showIdentitySearch;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  const t21 = selectedGroupId ?? null;
  let t22;
  if ($[34] !== groups || $[35] !== isFetching || $[36] !== onGroupClear || $[37] !== onGroupSelect || $[38] !== resolvedEmptyStateMessage || $[39] !== t21) {
    t22 = <SelectGroupModalItems groups={groups} selectedGroupId={t21} loading={isFetching} onGroupSelect={onGroupSelect} onGroupClear={onGroupClear} emptyStateMessage={resolvedEmptyStateMessage} />;
    $[34] = groups;
    $[35] = isFetching;
    $[36] = onGroupClear;
    $[37] = onGroupSelect;
    $[38] = resolvedEmptyStateMessage;
    $[39] = t21;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== bodyClasses || $[42] !== t22) {
    t23 = <div className={bodyClasses}>{t22}</div>;
    $[41] = bodyClasses;
    $[42] = t22;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== containerClasses || $[45] !== t19 || $[46] !== t20 || $[47] !== t23) {
    t24 = <div className={containerClasses}>{t19}{t20}{t23}</div>;
    $[44] = containerClasses;
    $[45] = t19;
    $[46] = t20;
    $[47] = t23;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  return t24;
}
function _temp() {}